<template>
  <a
    :href="unit.locked ? '/billing' : `/units/${unit.id}`"
    @click="onClick"
    class="flex flex-col h-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-500 transition-all relative"
    :class="[
      unit.locked ? 'opacity-75 hover:border-amber-300' : '',
      recommended ? 'ring-2 ring-emerald-500 border-transparent' : '',
      jumpAhead ? 'opacity-70' : '',
    ]"
  >
    <div class="flex items-start justify-between">
      <div>
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
            Unit {{ unit.position }}
          </span>
          <span v-if="recommended" class="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-full text-xs font-semibold px-2 py-0.5">
            &#9654; Up next
          </span>
          <span v-if="unit.locked" class="inline-flex items-center gap-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium px-2 py-0.5">
            &#128274; Pro
          </span>
        </div>
        <h3 class="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">{{ unit.title }}</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ unit.description }}</p>
      </div>
      <div class="text-right text-xs text-gray-400 whitespace-nowrap ml-4">
        <div>{{ unit.words_count }} words</div>
        <div>{{ unit.grammar_count }} grammar</div>
      </div>
    </div>

    <!-- Status + progress, pinned to the bottom so cards align regardless of content length -->
    <div class="mt-auto pt-3">
      <!-- Status badges -->
      <div class="flex flex-wrap gap-1.5">
        <span v-if="!learned && !practiced && !completed" class="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200 text-xs font-medium px-2 py-0.5 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
          Not started
        </span>
        <span v-if="learned" class="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-medium px-2 py-0.5 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
          &#128218; Learned
        </span>
        <span v-if="completed" class="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-xs font-medium px-2 py-0.5">
          &#10003; Completed
        </span>
        <span v-else-if="practiced" class="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-xs font-medium px-2 py-0.5 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800">
          &#127919; Practiced
        </span>
      </div>

      <!-- Progress bar: word mastery when SRS data exists (fraction of the
           unit's words with at least one successful review), otherwise the
           legacy best-session bar. Always rendered so card heights match. -->
      <div class="mt-3">
        <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span v-if="showMastery">{{ wordsStrong }}/{{ wordsTotal }} words strong</span>
          <span v-else-if="practiced">{{ progress.exercises_completed }} exercises done</span>
          <span v-else class="text-gray-400">No attempts yet</span>
          <span v-if="practiced">Best: {{ progress.best_score_pct }}%</span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            class="bg-emerald-500 h-1.5 rounded-full transition-all"
            :style="{ width: `${barPct}%` }"
          ></div>
        </div>
      </div>
    </div>
  </a>
</template>

<script setup>
import { computed } from 'vue'
import { showUpsell } from '../utils/upsell'

const props = defineProps({
  unit: { type: Object, required: true },
  progress: { type: Object, default: null },
  // The single suggested next unit on the dashboard.
  recommended: { type: Boolean, default: false },
  // Unstarted units past the recommended one: clicking asks before skipping ahead.
  jumpAhead: { type: Boolean, default: false },
})

const emit = defineEmits(['jump'])

const learned = computed(() => !!props.progress?.learned)
const completed = computed(() => !!props.progress?.completed)
// Any practice attempts at all, even below the completion threshold.
const practiced = computed(() => (props.progress?.exercises_completed || 0) > 0)

const wordsTotal = computed(() => props.progress?.words_total || 0)
const wordsStrong = computed(() => props.progress?.words_strong || 0)
// Level 0 words (letters) never enroll in SRS, so mastery only applies above it.
const showMastery = computed(() => wordsTotal.value > 0 && (props.unit.level || 0) > 0 && (learned.value || practiced.value))

const barPct = computed(() => {
  if (showMastery.value) return Math.min((wordsStrong.value / wordsTotal.value) * 100, 100)
  return practiced.value ? Math.min(props.progress.best_score_pct, 100) : 0
})

function onClick(e) {
  if (props.unit.locked) {
    // Show the upsell in place rather than navigating away. The href stays as
    // /billing so middle-click, right-click → open in new tab, and a broken JS
    // bundle all still reach the billing page.
    e.preventDefault()
    showUpsell({ unitId: props.unit.id, unitTitle: props.unit.title })
    return
  }
  if (props.jumpAhead) {
    e.preventDefault()
    emit('jump', props.unit)
  }
}
</script>
